import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { init, EChartsOption, SetOptionOpts, ECharts } from 'echarts';
import colors from 'assets/scss/colors.module.scss';
import _, { debounce } from 'lodash';
import { useStore } from 'app/context';

export interface ISankeyData {
	source: string;
	target: string;
	key?: string;
	value?: number;
	sourceColor?: string;
	targetColor?: string;
}

interface INodes {
	name: string;
	itemStyle?: {
		color: string;
	};
}
interface IConvertedData {
	nodes: INodes[];
	links: { source: string; target: string; value: number }[];
}

// add colours
const chartColors = [
	colors.blue,
	colors.yellow,
	colors.purple,
	colors.darkYellow,
	colors.green,
	colors.orange,
	colors.turquoise,
	colors.red,
	colors.richBlue,
	colors.richGreen,
	colors.richRed,
	colors.darkGreen,
	colors.richTurquoise,
	colors.brown,
];

const getChartColor = (nodes: INodes[]) => {
	const nodeColors = nodes.map((node) => node.itemStyle?.color);
	const availableColors = chartColors.filter((color) => !nodeColors.includes(color));
	return availableColors.length > 0 ? availableColors[0] : '#c99859';
};

function convertData(sankeyData: ISankeyData[]): IConvertedData {
	const result: IConvertedData = { nodes: [], links: [] };

	// Build nodes array preserving order from sankeyData
	// Use Map to track unique nodes while preserving insertion order
	const nodeMap = new Map<string, { name: string; color: string }>();

	// First pass: collect all nodes in order they appear (sources first, then targets)
	sankeyData.forEach((link) => {
		if (!nodeMap.has(link.source)) {
			nodeMap.set(link.source, { name: link.source, color: link.sourceColor || 'notacolor' });
		}
	});
	sankeyData.forEach((link) => {
		if (!nodeMap.has(link.target)) {
			nodeMap.set(link.target, { name: link.target, color: link.targetColor || 'notacolor' });
		}
	});

	// Convert to nodes array (Map preserves insertion order)
	result.nodes = Array.from(nodeMap.values()).map((node) => {
		return {
			name: node.name,
			itemStyle: { color: node.color },
		};
	});

	// Assign default colors to nodes without explicit colors
	result.nodes.forEach((node, index) => {
		if (node.itemStyle?.color === 'notacolor') {
			node.itemStyle = {
				color: getChartColor(result.nodes),
			};
		}
	});

	// Build links array
	sankeyData.forEach((node) => {
		result.links.push({
			source: node.source,
			target: node.target,
			value: node.value || 0,
		});
	});

	return result;
}

interface ISankeyChart {
	settings?: SetOptionOpts;
	loading?: boolean;
	data: ISankeyData[];
	opts?: EChartsOption;
	labelFormatter?: (params: any) => string;
	layoutIterations?: number;
}

export const SankeyChart = observer((props: ISankeyChart) => {
	const { uiStore } = useStore();
	const { settings, loading, data, labelFormatter, layoutIterations } = props;
	const chartRef = useRef<HTMLDivElement>(null);

	const printMode = uiStore.printMode;

	const chartHeight = printMode ? 350 : 500;

	useEffect(() => {
		const chartData = convertData(data);

		const defaultOpts: EChartsOption = {
			series: {
				type: 'sankey',
				data: chartData.nodes,
				links: chartData.links,
				animationDuration: 250,
				animationEasing: 'quadraticOut',
				animation: !printMode,
				emphasis: {
					focus: 'adjacency',
				},
				left: 0,
				right: 200,
				layoutIterations: layoutIterations !== undefined ? layoutIterations : 32,

				label: {
					formatter: labelFormatter || '{b} ({c})',
					fontSize: printMode ? 10 : 11,
					rich: {
						bold: {
							fontWeight: 'bold',
							fontSize: printMode ? 10 : 11,
						},
						italic: {
							fontStyle: 'italic',
							fontSize: printMode ? 10 : 11,
						},
					},
				},
			},
		};

		const chartOptions: EChartsOption = _.merge({}, defaultOpts, props.opts);

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg' });
			chart.setOption(chartOptions, settings);
		}

		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, data, settings, labelFormatter, props.opts, layoutIterations, printMode]);

	useEffect(() => {
		return;
		/*
        // Update chart
        if (chartRef.current !== null) {
          const chart = getInstanceByDom(chartRef.current);
          if (chart) {
            chart.setOption(option, settings);
          }
        }
        */
	}, [chartRef, settings]);

	useEffect(() => {
		return;
		// Update chart
		/*
        if (chartRef.current !== null) {
          const chart = getInstanceByDom(chartRef.current);
          loading === true ? chart!.showLoading() : chart!.hideLoading();
        }
        */
	}, [loading]);

	return <div style={{ width: '100%', height: `${chartHeight}px`, background: '#ffffff', breakInside: 'avoid' }} ref={chartRef}></div>;
});
